import React, { useEffect, useRef, useState } from 'react';
import Grid from '../../shared/Grid/Grid';
import { AuditLog, AuditLogGroup } from '../../../models/entities/audit/auditLog';
import { ColumnType } from '../../shared/Grid/types/Column';
import { styled } from '../../shared/Theme/theme';
import Button from '../../shared/SmallComponents/Button';
import FilterPopover from './components/Filter/FilterPopover';
import FiltersStripe from './components/Filter/FiltersStripe';
import TextInput from '../../shared/inputs/base/TextInput';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Loader from '../../shared/SmallComponents/Loader';
import { AuditLogFilters } from '../../../models/entities/audit/auditLog';
import GroupedAuditTable from './components/GroupedAuditTable';

type Props = {
    localization: any;
    showLoader?: boolean;
    loading: boolean;
    fetchGroupedAuditLogs: (filters?: AuditLogFilters, loadMore?: boolean) => void;
    fetchMetadata: () => void;
    groupedAuditLogs: Array<AuditLogGroup>;
    groupedPagination: {
        totalGroups: number;
        hasMore: boolean;
        limit: number;
        offset: number;
    };
    currentFilters: AuditLogFilters;
};

const AuditDashboardPage = ({
    localization,
    showLoader,
    loading,
    fetchGroupedAuditLogs,
    fetchMetadata,
    groupedAuditLogs,
    groupedPagination,
    currentFilters
}: Props) => {
    const initialFetchDone = useRef(false);
    const [searchTerm, setSearchTerm] = useState(currentFilters.searchTerm || '');
    const [isTypingSearch, setIsTypingSearch] = useState(false);

    useEffect(() => {
        if (!initialFetchDone.current) {
            initialFetchDone.current = true;
            fetchGroupedAuditLogs({
                limit: 10,
                sortBy: 'created_at',
                sortOrder: 'DESC'
            });
            fetchMetadata();
        }
    }, [fetchGroupedAuditLogs, fetchMetadata]);

    useEffect(() => {
        setIsTypingSearch(true);
        const timeout = setTimeout(() => {
            const filters: AuditLogFilters = {
                ...currentFilters,
                searchTerm: searchTerm || undefined
            };
            // Remove searchTerm from filters if empty
            if (!searchTerm) {
                delete filters.searchTerm;
            }
            fetchGroupedAuditLogs(filters, false);
            setIsTypingSearch(false);
        }, 500);

        return () => clearTimeout(timeout);
    }, [searchTerm]);

    const showTopLoader = groupedAuditLogs.length > 0;

    // Pagination handlers
    const handlePrevious = () => {
        const newOffset = Math.max(0, groupedPagination.offset - groupedPagination.limit);
        fetchGroupedAuditLogs({
            ...currentFilters,
            offset: newOffset,
            limit: groupedPagination.limit
        });
    };

    const handleNext = () => {
        const newOffset = groupedPagination.offset + groupedPagination.limit;
        fetchGroupedAuditLogs({
            ...currentFilters,
            offset: newOffset,
            limit: groupedPagination.limit
        });
    };

    // Calculate display range
    const startRange = groupedPagination.offset + 1;
    const endRange = Math.min(
        groupedPagination.offset + groupedPagination.limit,
        groupedPagination.totalGroups
    );
    const hasPrevious = groupedPagination.offset > 0;
    const hasNext = groupedPagination.hasMore;

    return (
        <PageWrapper>
            {!!showLoader && showTopLoader && (
                <LoadingOverlay>
                    <Loader width="20px" marginTop="0" inline />
                </LoadingOverlay>
            )}
            <PageHeader>
                <Title>{localization?.title || 'Audit Trail (Grouped)'}</Title>

                <FiltersStripe localization={localization} />

                <HeaderRightSide>
                    {groupedPagination.totalGroups > 0 && (
                        <NavButtonsContainer>
                            <IconButton
                                className="nav-btn"
                                onClick={handlePrevious}
                                disabled={!hasPrevious || loading}
                                size="small"
                            >
                                <span className="material-icons">navigate_before</span>
                            </IconButton>
                            <span>
                                {startRange} – {endRange} of {groupedPagination.totalGroups}
                            </span>
                            <IconButton
                                className="nav-btn"
                                onClick={handleNext}
                                disabled={!hasNext || loading}
                                size="small"
                            >
                                <span className="material-icons">navigate_next</span>
                            </IconButton>
                        </NavButtonsContainer>
                    )}
                    <Button
                        width="32px"
                        height="32px"
                        borderRadius="50%"
                        margin="7px"
                        buttonType="hollow"
                        onClick={() => fetchGroupedAuditLogs()}
                    >
                        <Tooltip title={'Refresh'} aria-label="refresh">
                            <span className="material-icons">refresh</span>
                        </Tooltip>
                    </Button>
                    <TextInput
                        style={{
                            height: '30px',
                            margin: '4px',
                            width: 'auto'
                        }}
                        allowClear={true}
                        onChange={(value: string) => setSearchTerm(value)}
                        placeHolder="Search users, entities, field names..."
                        value={searchTerm || ''}
                        suffix={isTypingSearch && <Loader wrapperWidth="auto" showText={false} marginTop="0" width="15px" />}
                    />
                    <FilterPopover localization={localization?.filter || {}} />
                </HeaderRightSide>
            </PageHeader>
            <GridWrapper>
                <GroupedAuditTable
                    groups={groupedAuditLogs}
                    loading={loading}
                />
            </GridWrapper>
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const GridWrapper = styled.div`
    flex: 1;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    z-index: 1;
    overflow: overlay;

    @media screen and (min-width: 768px) {
        height: calc(100% - 127px);
    }

    @media screen and (max-width: 768px) {
        height: calc(100% - 135px - 40px);
    }

    @media screen and (max-height: 400px) {
        height: calc(100% - 135px);
    }
`;

const PageHeader = styled.div`
    display: flex;
    padding: 20px 12px 0 12px;
    align-items: center;

    @media screen and (max-width: 768px) {
        justify-content: center;
    }

    position: sticky;
    top: 0px;
    z-index: 1000;
    background-color: #fff;
`;

const Title = styled.div`
    display: flex;
    color: ${(props) => props.theme.colors.pageTitle};
    font-size: ${(props) => props.theme.fontSizes.pageTitle};
    font-weight: bold;

    @media screen and (max-width: 768px) {
        display: none;
    }
`;

const HeaderRightSide = styled.div`
    display: flex;
    flex-direction: columm;
    width: 100%;
    max-width: fit-content;
    align-items: center;

    @media screen and (max-width: 768px) {
        height: 40px;
        justify-content: center;
    }
`;

const NavButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    margin: 0 15px;
    color: ${(props) => props.theme.colors.primaryBlue};
    font-weight: bold;
    .nav-btn {
        font-size: 1.4rem;
        color: ${(props) => props.theme.colors.primaryBlue};
    }
`;

const LoadingOverlay = styled.div`
    background-color: #edf8f9;
    padding: 6px 10px;
    border-radius: 7px;
    position: absolute;
    z-index: 1012;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
`;

export default AuditDashboardPage;
